import { useFormContext } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRef } from "react";
import { Calendar, Clock, FileText } from "lucide-react";
import { cn } from "@/lib/utils";

export default function NextVisitSection() {
  const { setValue, watch } = useFormContext();
  const refs = useRef<(HTMLInputElement | HTMLTextAreaElement | null)[]>([]);
  
  const nextVisit = watch("next_visit") || {};

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === "Enter") {
      e.preventDefault();
      refs.current[index + 1]?.focus();
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("next_visit.date", e.target.value, { shouldDirty: true });
  };

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue("next_visit.time", e.target.value, { shouldDirty: true });
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setValue("next_visit.notes", e.target.value, { shouldDirty: true });
  };

  const handleDateIconClick = () => {
    const dateInput = refs.current[0] as HTMLInputElement;
    if (dateInput) {
      dateInput.showPicker?.();
      if (!dateInput.showPicker) {
        dateInput.focus();
        dateInput.click();
      }
    }
  };

  const handleTimeIconClick = () => {
    const timeInput = refs.current[1] as HTMLInputElement;
    if (timeInput) {
      timeInput.showPicker?.();
      if (!timeInput.showPicker) {
        timeInput.focus();
        timeInput.click();
      }
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
      {/* Date Input with Icon */}
      <div className="col-span-3">
        <label className="block text-xs font-medium text-muted-foreground mb-1">Date</label>
        <div className="relative">
          <Calendar 
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground cursor-pointer z-10" 
            onClick={handleDateIconClick}
          />
          <Input
            type="date"
            value={nextVisit.date || ""}
            onChange={handleDateChange}
            className="h-8 text-sm border-input pl-8 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            ref={(el) => { refs.current[0] = el; }}
            onKeyDown={(e) => handleKeyDown(e, 0)}
          />
        </div>
      </div>

      {/* Time Input with Icon */}
      <div className="col-span-2">
        <label className="block text-xs font-medium text-muted-foreground mb-1">Time</label>
        <div className="relative">
          <Clock 
            className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground cursor-pointer z-10" 
            onClick={handleTimeIconClick}
          />
          <Input
            type="time"
            value={nextVisit.time || ""}
            onChange={handleTimeChange}
            className="h-8 text-sm border-input pl-8 [&::-webkit-calendar-picker-indicator]:opacity-0 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:cursor-pointer"
            ref={(el) => { refs.current[1] = el; }}
            onKeyDown={(e) => handleKeyDown(e, 1)}
          />
        </div>
      </div>
      
      {/* Notes Textarea with Icon */}
      <div className="col-span-7">
        <label className="block text-xs font-medium text-muted-foreground mb-1">Notes</label>
        <div className="relative">
          <Textarea
            value={nextVisit.notes || ""}
            onChange={handleNotesChange}
            placeholder="Visit notes..."
            className="h-8 text-sm border-input resize-none pl-4"
            ref={(el) => { refs.current[2] = el; }}
          />
        </div>
      </div>
    </div>
  );
}